// §4.5 CYCLE COUNT WORKSHEET (floor) — spec 06 §3.4 step 3.
//
// THE INVARIANT THIS SCREEN EXISTS TO KEEP: a blind count worksheet renders NO
// EXPECTED VALUE. Not hidden behind a toggle, not greyed out, not in a `title`
// attribute — absent. §5 row 38 makes that structurally true on the wire
// (`expected_qty_base` is OMITTED, field-absent rather than null, while blind ∧
// in_progress ∧ the caller lacks `count.review`) and §9.12 says the stripping
// is server-side "not hidden client-side". This screen's job is not to
// re-implement that rule — it is to never undo it:
//
//   · it reads `expected_qty_base` NOWHERE (asserted by a unit test that
//     renders a worksheet whose lines DO carry the field and proves the number
//     never reaches the DOM — because the day a reviewer opens this screen, the
//     server WILL send it);
//   · the offline snapshot is stored verbatim (floor-read-cache.ts), so a
//     cached blind worksheet is exactly as blind as the response was;
//   · there is no "variance so far" figure anywhere, because computing one
//     requires the expected value and would leak it by arithmetic.
//
// The counting model, per §3.4 step 3:
//   scan bin → the lines in that bin → per line enter counted qty/measure →
//   "found extra item" adds a line with expected 0 → progress → submit.
//
// §5 row 40's UoM rule is enforced at the input: a stock-unit line measured in
// the LINE's UoM must record `counted_measure` (a qty-base-only record is 422),
// and a bulk line records `counted_qty_base`. The screen sends the right field
// rather than letting the operator discover the difference through a refusal
// that arrives an hour later, out of a dead zone.
//
// OFFLINE: the whole worksheet is cached on the last online read, every record
// queues through the outbox with a tap-time key, and re-recording a line
// SUPERSEDES the still-queued value (§5 row 40's "a differing one overwrites")
// with a fresh key rather than stacking two mutations.

import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  FloorButton,
  FloorEmpty,
  FloorError,
  FloorNeedsConnection,
  FloorNotSaved,
  FloorScreen,
  FloorSkeleton,
  SnapshotNote,
} from "../components/floor-chrome";
import { ScanInput } from "../components/scan-input";
import { floorReadSignal, useCachedRead } from "../hooks/use-cached-read";
import { floorOutbox, useFloorOutbox, useFloorSubmit } from "../hooks/use-floor-outbox";
import { useFloorDraft } from "../hooks/use-floor-draft";
import { readCacheKey } from "../lib/floor-read-cache";
import { deviceId } from "../lib/floor-device";
import { floorFeedback } from "../lib/floor-feedback";
import { floorFailureMessage, floorRequestId } from "../lib/floor-errors";
import { useInventoryPermissions } from "../../office/inventory/hooks/permissions";
import { client } from "../../../lib/api";
import { hasLineage } from "../../../lib/role-codes";
import { refreshQuery } from "../../../lib/query";
import { unwrap } from "../../../lib/request";
import { unitSymbol } from "../../../lib/quantity";
import { shortId } from "../../../lib/short-id";
import type { OutboxItem } from "../lib/floor-outbox";
import type { components } from "@assessly/api-client";
import { enumLabel } from "../../../lib/enum-label";

type S = components["schemas"];
type CycleCountLine = S["CycleCountLine"];

const MEASURE_RE = /^\d{1,12}(\.\d{1,6})?$/u;

/**
 * A server-echoed count, made fit for the EDIT BUFFER it prefills.
 *
 * The wire is canonical `numeric(18,6)`: a counter who typed `1250` is echoed
 * `"1250.000000"`, and handing those six zeros back into the input makes the
 * wire's representation the counter's problem. But this is an edit buffer, not
 * display text — whatever sits in it is exactly what Record re-sends, so it
 * must stay inside `MEASURE_RE`. That rules out `formatMeasure` (its locale
 * grouping — "1,250" — is a 422, not a measure), so the derivation here is the
 * minimal wire-safe one: trim the always-zero tail, keep a real fraction
 * (`"1250.500000"` → `"1250.5"`). Anything without a fraction part (including
 * text that is not a measure at all) passes through verbatim. Pure + exported
 * so the rule is pinned by the worksheet test, not this comment.
 */
export function trimMeasureTail(text: string): string {
  if (!text.includes(".")) return text;
  return text.replace(/0+$/u, "").replace(/\.$/u, "");
}

/** The list endpoint's own cap on both `id=` and `limit` (spec 06 §5 row 1). */
const LOCATION_ID_BATCH = 200;

/**
 * Which bin a scanned payload names, matched LOCALLY against the worksheet.
 *
 * A canonical location QR carries the location UUID (§9.9); a printed rack
 * label carries the human path ("WH1/A/A-03"). Both are accepted, and nothing
 * else is guessed — an unmatched payload is reported, never silently ignored,
 * because a counter who thinks they changed bin and did not counts the wrong
 * rack. Pure and exported so the matching is a tested rule.
 */
export function matchBin(
  payload: string,
  locationIds: readonly string[],
  paths: Readonly<Record<string, string>>,
): string | undefined {
  const needle = payload.trim().toLowerCase();
  if (needle.length === 0) return undefined;
  for (const id of locationIds) {
    if (needle === id.toLowerCase() || needle.includes(id.toLowerCase())) return id;
  }
  for (const id of locationIds) {
    if (paths[id]?.toLowerCase() === needle) return id;
  }
  return undefined;
}

/**
 * The composite the worksheet needs offline: the count, its lines, and the
 * NAMES its lines reference. Resolved in one queryFn so the whole thing lands
 * in the offline snapshot together — a worksheet cached without its serials
 * would show a counter a column of uuids in a dark aisle.
 */
interface WorksheetSnapshot {
  readonly count: S["CycleCount"];
  readonly lines: readonly CycleCountLine[];
  readonly productNames: Readonly<Record<string, string>>;
  readonly unitSerials: Readonly<Record<string, string>>;
  readonly locationPaths: Readonly<Record<string, string>>;
}

/** Exported for the unit test that pins the location resolution against
 * regressing to a page scan — the snapshot is what a counter sees in a dead
 * zone, so what it does or does not contain is a claim worth testing directly
 * rather than through a mounted screen. */
export async function loadWorksheet(id: string, signal?: AbortSignal): Promise<WorksheetSnapshot> {
  const detail = unwrap(
    await client.GET("/v1/inventory/cycle-counts/{id}", {
      params: { path: { id } },
      signal: floorReadSignal(signal),
    }),
    "cycle-count",
  );
  const productIds = [...new Set(detail.lines.map((line) => line.product_id))];
  const unitIds = [
    ...new Set(
      detail.lines.map((line) => line.stock_unit_id).filter((v): v is string => v !== null),
    ),
  ];

  const productNames: Record<string, string> = {};
  const unitSerials: Record<string, string> = {};
  const locationPaths: Record<string, string> = {};

  // The bins this worksheet NAMES, and no others. Fetching one 200-row page of
  // the tenant's tree (what this did until 2026-08-05) is both more work and
  // wrong: a count over an aisle whose bins sort past row 200 rendered its
  // location column blank, on a phone, in an aisle, with no way to tell whether
  // the line belonged to the rack in front of the counter. §5 row 1's `id=` asks
  // for exactly the ids, and the server sizes the page from the id count.
  const locationIds = [...new Set(detail.lines.map((line) => line.location_id))];
  if (!locationIds.includes(detail.count.scope_location_id)) {
    locationIds.push(detail.count.scope_location_id);
  }
  const locationBatches: string[][] = [];
  for (let i = 0; i < locationIds.length; i += LOCATION_ID_BATCH) {
    locationBatches.push(locationIds.slice(i, i + LOCATION_ID_BATCH));
  }

  // Per-id reads for products/units, exactly like the office `useProductIndex`
  // pattern: Module 01 has no batch product read, and inventing an unbounded
  // list call here would be worse than N cached ones.
  await Promise.all([
    ...productIds.map(async (pid) => {
      try {
        const product = unwrap(
          await client.GET("/v1/catalogue/products/{id}", {
            params: { path: { id: pid } },
            signal: floorReadSignal(signal),
          }),
          "product",
        );
        productNames[pid] = `${product.sku} · ${product.name}`;
      } catch {
        // A name that will not resolve is rendered as a short id chip, never as
        // a blank — the line still has to be countable.
      }
    }),
    ...unitIds.map(async (uid) => {
      try {
        const card = unwrap(
          await client.GET("/v1/inventory/stock-units/{id}", {
            params: { path: { id: uid } },
            signal: floorReadSignal(signal),
          }),
          "stock-unit",
        );
        unitSerials[uid] = card.unit.serial_code;
      } catch {
        /* same */
      }
    }),
    ...locationBatches.map(async (batch) => {
      try {
        const locations = unwrap(
          await client.GET("/v1/inventory/locations", {
            params: { query: { id: batch, limit: LOCATION_ID_BATCH } },
            signal: floorReadSignal(signal),
          }),
          "locations",
        );
        for (const row of locations.items) locationPaths[row.id] = row.path;
      } catch {
        /* same */
      }
    }),
  ]);

  return { count: detail.count, lines: detail.lines, productNames, unitSerials, locationPaths };
}

/** The worksheet read's query key. Named once because the screen both READS it
 * and, when the queue drains, has to REFRESH it — two spellings of one key is
 * how a refresh silently targets nothing. */
export const countWorksheetQueryKey = (countId: string) => ["floor", "count", countId] as const;

/** Does this queued operation act on THIS worksheet? `count_record`,
 * `count_found` and `count_submit` name the count directly; the "found an extra
 * bobbin" SCAN names it through the follow-up it carries (floor-ops.ts), and
 * that scan's whole purpose is to add a line to this sheet. */
export function actsOnCount(item: OutboxItem, countId: string): boolean {
  if ("countId" in item.op && item.op.countId === countId) return true;
  return item.followUp?.countId === countId;
}

export function FloorCountWorksheetScreen() {
  const { t } = useTranslation();
  const { id } = useParams({ from: "/floor/counts/$id" });
  const perms = useInventoryPermissions();
  const outbox = useFloorOutbox();
  const queue = floorOutbox();
  const submitHook = useFloorSubmit();
  const [bin, setBin] = useState<string | undefined>(undefined);
  const [binNote, setBinNote] = useState<string | undefined>(undefined);
  const [foundNote, setFoundNote] = useState<string | undefined>(undefined);

  const read = useCachedRead<WorksheetSnapshot>({
    queryKey: countWorksheetQueryKey(id),
    cacheKey: readCacheKey("count", id),
    queryFn: ({ signal }) => loadWorksheet(id, signal),
  });

  const snapshot = read.data;

  // ══ THE DRAIN IS A SERVER WRITE THIS SCREEN CANNOT SEE ═══════════════════
  //
  // Every number on this screen is `server ⊕ still-queued` (see `queued`
  // below), and the two halves change at DIFFERENT moments: the queued half
  // disappears the instant the outbox drains an item, while the server half is
  // a TanStack read that nothing was invalidating. So the moment a count
  // record landed on the server — the moment the worksheet became MORE
  // complete — the overlay vanished and the screen fell back to the snapshot it
  // fetched before any of it was recorded: twelve counted lines rendered as
  // "0 of 12 counted", every input blank, Submit disabled, and no way out but a
  // manual reload. That is the whole of defect #95, and it is not a staleness
  // budget to shorten: no `staleTime` was ever the cause and no refetch was
  // ever going to be scheduled.
  //
  // The rule is the one op-run already follows for the station snapshot (see
  // `useStationSnapshotRefresh`): a tap the server APPLIED invalidates the read
  // that describes it. It is stated HERE, over the queue, rather than in the
  // record button's own handler, because the apply does not have to happen
  // while anyone is looking — a bin counted in a dead zone drains minutes
  // later, from the background, with no promise left to hang a `.then` on. What
  // the screen can always observe is an item LEAVING the pending queue without
  // being parked, and that is exactly "the server took it".
  //
  // `refreshQuery` (cancel, then invalidate) rather than a bare invalidate: the
  // worksheet read can be in flight when a drain settles, and an invalidate
  // over an in-flight first fetch coalesces instead of refetching (lib/query.ts
  // documents the react-query internal). A stale composite read winning that
  // race would reinstate the very state this effect exists to clear.
  const qc = useQueryClient();
  const queueForThisCount = useMemo(() => {
    const inFlight = new Set<string>();
    for (const item of outbox.pending) {
      if (actsOnCount(item, id)) inFlight.add(item.clientKey);
    }
    // Still on the DEVICE, not on the server: a parked (or held) record has not
    // changed anything to re-read, and refreshing on it would only pull the
    // pre-record snapshot back over a line the operator is being asked to fix.
    const held = new Set<string>();
    for (const item of [...outbox.needsAttention, ...outbox.needsReview, ...outbox.foreign]) {
      if (actsOnCount(item, id)) held.add(item.clientKey);
    }
    return { inFlight, held };
  }, [outbox.pending, outbox.needsAttention, outbox.needsReview, outbox.foreign, id]);

  const wasInFlight = useRef<ReadonlySet<string>>(queueForThisCount.inFlight);
  useEffect(() => {
    const before = wasInFlight.current;
    wasInFlight.current = queueForThisCount.inFlight;
    let drained = false;
    for (const key of before) {
      if (!queueForThisCount.inFlight.has(key) && !queueForThisCount.held.has(key)) {
        drained = true;
        break;
      }
    }
    if (drained) void refreshQuery(qc, countWorksheetQueryKey(id));
  }, [queueForThisCount, qc, id]);

  /**
   * A line's LOCAL state = the server's value overlaid with anything still in
   * the queue for it. Without this overlay a counter who records 12 lines in a
   * dead zone watches every one of them snap back to blank, which is how a bin
   * gets counted twice.
   */
  const queued = useMemo(() => {
    const map = new Map<string, { value: string | undefined; note: string | undefined }>();
    // ONLY items that are still PENDING. `outbox.items` is the union of pending
    // AND needs_attention, so counting a parked record as recorded would let a
    // line whose 422/403 refusal is sitting in the tray render as counted and
    // flip Submit to enabled — the operator's own screen contradicting the tray,
    // for a count the server will refuse an hour later.
    for (const item of outbox.pending) {
      if (item.op.kind !== "count_record" || item.op.countId !== id) continue;
      map.set(item.op.lineId, {
        value: item.op.body.counted_measure ?? item.op.body.counted_qty_base,
        note: item.op.body.note,
      });
    }
    return map;
  }, [outbox.pending, id]);

  /** Lines whose record is PARKED — excluded from progress, linked to the tray. */
  const parked = useMemo(() => {
    const set = new Set<string>();
    for (const item of outbox.needsAttention) {
      if (item.op.kind === "count_record" && item.op.countId === id) set.add(item.op.lineId);
    }
    return set;
  }, [outbox.needsAttention, id]);

  const lines = snapshot?.lines ?? [];
  const byLocation = useMemo(() => {
    const groups = new Map<string, CycleCountLine[]>();
    for (const line of lines) {
      const list = groups.get(line.location_id) ?? [];
      list.push(line);
      groups.set(line.location_id, list);
    }
    return groups;
  }, [lines]);

  /**
   * §3.4 step 4 and §5 row 42: a line is SETTLED when it is counted **or
   * explicitly skipped with a note**. The server implements exactly that (the
   * submit refuses only lines that are `pending` with no note), so a client that
   * required a number left an operator who skipped one line with "bin blocked
   * by pallet" unable to submit at all — before or after the drain.
   */
  const settledCount = lines.filter((line) => {
    if (parked.has(line.id)) return false;
    const pending = queued.get(line.id);
    if (pending !== undefined) {
      return (pending.value ?? "").length > 0 || (pending.note ?? "").trim().length > 0;
    }
    return (
      line.counted_qty_base !== null ||
      line.counted_measure !== null ||
      (line.note ?? "").trim().length > 0
    );
  }).length;
  const counted = settledCount;
  const complete = lines.length > 0 && counted === lines.length;

  /**
   * §7 "floor_worker: count only" — the server narrows create/start/SUBMIT to
   * group_admin | facility_admin | storekeeper. The permission mirror cannot
   * express a narrowing inside `inventory.count.perform`, so the roles are read
   * directly, on the LINEAGE the service reads (`cycle-counts.service.ts`
   * UNRESTRICTED_COUNT_ROLES, D-224), so a tenant's clone of storekeeper may
   * submit (D-586). Without this a floor worker finishes twelve lines and taps
   * a button that parks a PERMISSION_DENIED in the tray.
   */
  const maySubmit = hasLineage(perms, ["group_admin", "facility_admin", "storekeeper"]);

  const settlement = submitHook.state.phase === "settled" ? submitHook.state.settlement : undefined;

  return (
    <FloorScreen
      title={snapshot?.count.count_code ?? t("inventory.floor.count.title")}
      back={
        <Link to="/floor/counts" className="floor-back">
          {t("inventory.floor.back")}
        </Link>
      }
      actions={
        snapshot === undefined ? undefined : maySubmit ? (
          <FloorButton
            variant="primary"
            disabled={!complete || submitHook.state.phase === "submitting"}
            onClick={() => {
              void submitHook
                .submit({ kind: "count_submit", countId: id }, undefined, {
                  identity: snapshot.count.count_code,
                })
                .then((settled) => {
                  floorFeedback(settled === undefined ? "error" : "ok");
                });
            }}
            testId="floor-count-submit"
          >
            {complete
              ? t("inventory.floor.count.submit")
              : t("inventory.floor.count.submitBlocked", {
                  remaining: lines.length - counted,
                })}
          </FloorButton>
        ) : (
          <p className="floor-actionbar-note" data-testid="floor-count-submit-denied">
            {complete
              ? t("inventory.floor.count.handOff")
              : t("inventory.floor.count.submitBlocked", { remaining: lines.length - counted })}
          </p>
        )
      }
    >
      <SnapshotNote
        source={read.source}
        cachedAt={read.cachedAt}
        revalidating={read.revalidating}
      />
      {read.isLoading ? <FloorSkeleton rows={4} /> : null}

      {read.source === "none" && !read.isLoading ? (
        read.online ? (
          <FloorError
            title={t("inventory.floor.count.loadFailedTitle")}
            detail={t("inventory.floor.count.loadFailedBody")}
            requestId={floorRequestId(read.error)}
            onRetry={read.refetch}
          />
        ) : (
          <FloorNeedsConnection what={t("inventory.floor.count.thisWorksheet")} />
        )
      ) : null}

      {snapshot !== undefined ? (
        <>
          <div className="floor-count-head">
            {snapshot.count.blind ? (
              <p className="floor-badge floor-badge-warn" data-testid="floor-count-blind-badge">
                {t("inventory.floor.count.blindBadge")}
              </p>
            ) : null}
            <p className="floor-hint" data-testid="floor-count-blind-note">
              {snapshot.count.blind
                ? t("inventory.floor.count.blindNote")
                : t("inventory.floor.count.openNote")}
            </p>
            <p className="floor-progress" data-testid="floor-count-progress">
              {t("inventory.floor.count.progress", { counted, total: lines.length })}
            </p>
            <progress value={counted} max={Math.max(1, lines.length)} />
          </div>

          {snapshot.count.status !== "in_progress" ? (
            <p className="floor-hint floor-hint-warn" data-testid="floor-count-closed">
              {t("inventory.floor.count.notCounting", {
                status: enumLabel(t, "inventory.vocab.count", snapshot.count.status),
              })}
            </p>
          ) : null}

          {!perms.has("inventory.count.perform") && perms.isResolved && !perms.isFeedError ? (
            <p className="floor-hint floor-hint-warn" data-testid="floor-count-nopermission">
              {t("inventory.floor.count.noPermission")}
            </p>
          ) : null}

          {/* §3.4 step 3 / §4.5: "scan bin → the items in it". The scan is the
              primary motion — a bin QR is on the rack and the operator is
              already holding the scanner — and the tap list stays as the
              fallback for a damaged label (§9.15). Matching is LOCAL, against
              the paths and ids already on the worksheet, so it works with no
              signal, which is the whole point of counting offline. */}
          <section className="floor-panel">
            <h2 className="floor-panel-head">{t("inventory.floor.count.binHead")}</h2>
            <ScanInput
              autoFocus={false}
              captureWedge
              label={t("inventory.floor.count.binScanLabel")}
              placeholder={t("inventory.floor.count.binScanPlaceholder")}
              onScan={(payload) => {
                const match = matchBin(payload, [...byLocation.keys()], snapshot.locationPaths);
                if (match === undefined) {
                  setBinNote(t("inventory.floor.count.binNoMatch", { payload }));
                  floorFeedback("error");
                  return;
                }
                setBin(match);
                setBinNote(undefined);
                floorFeedback("ok");
              }}
            />
            {binNote !== undefined ? (
              <p className="floor-scan-error" role="alert" data-testid="floor-bin-nomatch">
                {binNote}
              </p>
            ) : null}
            <div className="floor-bin-picker">
              {[...byLocation.keys()].map((locationId) => (
                <FloorButton
                  key={locationId}
                  variant={bin === locationId ? "primary" : "default"}
                  onClick={() => {
                    setBin(bin === locationId ? undefined : locationId);
                  }}
                  testId="floor-bin-button"
                >
                  {snapshot.locationPaths[locationId] ?? shortId(locationId)}
                </FloorButton>
              ))}
            </div>
          </section>

          {lines.length === 0 ? (
            <FloorEmpty
              title={t("inventory.floor.count.emptyTitle")}
              body={t("inventory.floor.count.emptyBody")}
            />
          ) : null}

          <ul className="floor-lines" data-testid="floor-count-lines">
            {lines
              .filter((line) => bin === undefined || line.location_id === bin)
              .map((line) => (
                <CountLineRow
                  key={line.id}
                  countId={id}
                  line={line}
                  queued={queued.get(line.id)}
                  parked={parked.has(line.id)}
                  productName={snapshot.productNames[line.product_id]}
                  serial={
                    line.stock_unit_id !== null
                      ? snapshot.unitSerials[line.stock_unit_id]
                      : undefined
                  }
                  locationPath={snapshot.locationPaths[line.location_id]}
                />
              ))}
          </ul>

          <section className="floor-panel" data-testid="floor-found-panel">
            <h2 className="floor-panel-head">{t("inventory.floor.count.foundHead")}</h2>
            <p className="floor-hint">{t("inventory.floor.count.foundHint")}</p>
            {bin === undefined ? (
              <p className="floor-hint floor-hint-warn" data-testid="floor-found-needs-bin">
                {t("inventory.floor.count.foundNeedsBin")}
              </p>
            ) : (
              <ScanInput
                autoFocus={false}
                label={t("inventory.floor.count.foundLabel")}
                placeholder={t("inventory.floor.count.foundPlaceholder")}
                onScan={(payload) => {
                  // §4.5 / §5 row 41: the found item becomes a COUNT LINE. Only
                  // the server can say which unit a label is (§9.9), so the scan
                  // carries a persisted FOLLOW-UP: when it lands — now, or after
                  // a dead zone, or after a restart — the engine derives
                  // `POST /cycle-counts/{id}/lines` from the response and queues
                  // it. The bin and the device id ride along, because a count
                  // whose whole purpose is bin-accurate must not create a line
                  // with no location attribution (§3.5 rung 1/2).
                  void queue
                    .enqueue(
                      {
                        kind: "scan",
                        body: {
                          scanned_payload: payload,
                          action: "count",
                          app_area: "floor",
                          device_id: deviceId(),
                          context_location_id: bin,
                        },
                      },
                      {
                        identity: snapshot.locationPaths[bin] ?? payload,
                        followUp: { kind: "count_found", countId: id, locationId: bin },
                      },
                    )
                    .then(() => {
                      setFoundNote(t("inventory.floor.count.foundQueued"));
                      floorFeedback("ok");
                    })
                    .catch(() => {
                      setFoundNote(t("inventory.floor.count.foundNotSaved"));
                      floorFeedback("error");
                    });
                }}
              />
            )}
            {foundNote !== undefined ? (
              <p className="floor-hint" role="status" data-testid="floor-found-note">
                {foundNote}
              </p>
            ) : null}
          </section>

          {settlement?.outcome === "queued" ? (
            <p className="floor-result-queued" role="status" data-testid="floor-count-queued">
              {t("inventory.floor.count.submitQueued")}
            </p>
          ) : null}
          {settlement?.outcome === "needs_attention" ? (
            <FloorError
              title={t("inventory.floor.count.submitFailedTitle")}
              detail={floorFailureMessage(t, settlement.failure)}
              requestId={settlement.failure.requestId}
            />
          ) : null}
          {settlement?.outcome === "applied" ? (
            <p className="floor-result-body" role="status" data-testid="floor-count-submitted">
              {t("inventory.floor.count.submitted")}
            </p>
          ) : null}
          {submitHook.state.phase === "not_saved" ? (
            <FloorNotSaved onRetry={submitHook.reset} />
          ) : null}
        </>
      ) : null}
    </FloorScreen>
  );
}

/**
 * One worksheet line.
 *
 * NOTHING in this component reads `line.expected_qty_base`. That is the blind
 * invariant, and it is pinned by a test that feeds this component a line
 * CARRYING an expected value and asserts it never appears.
 */
export function CountLineRow({
  countId,
  line,
  queued,
  parked,
  productName,
  serial,
  locationPath,
}: {
  countId: string;
  line: CycleCountLine;
  queued: { value: string | undefined; note: string | undefined } | undefined;
  parked: boolean;
  productName: string | undefined;
  serial: string | undefined;
  locationPath: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  // The unit as the counter reads it ("mm²", "шт."), never its pack code.
  const uomLabel = unitSymbol(line.uom, { locale: i18n.language });
  const queue = floorOutbox();
  // Trimmed at the seam: the counter's OWN number is `1250`, and the `.000000`
  // is the wire's canonicalisation, not something they typed. The queued value
  // is NOT trimmed — that is literally the text the operator entered.
  const server = trimMeasureTail(line.counted_measure ?? line.counted_qty_base ?? "");
  // The typed value survives a reload: a bin counted line by line over twenty
  // minutes must not evaporate because the OS reclaimed the tab.
  const draft = useFloorDraft<{ value: string; note: string }>(`count:${countId}:${line.id}`, {
    value: queued?.value ?? server,
    note: queued?.note ?? line.note ?? "",
  });
  const { value, note } = draft.value;
  const [invalid, setInvalid] = useState(false);
  const [state, setState] = useState<"idle" | "saving" | "saved" | "failed">("idle");

  // §5 row 40: a stock-unit line records `counted_measure`; a bulk line records
  // `counted_qty_base`. Sending the wrong one is a 422 the operator would only
  // discover after the dead zone ended.
  const isUnitLine = line.stock_unit_id !== null;

  async function record(): Promise<void> {
    const text = value.trim();
    if (text.length > 0 && !MEASURE_RE.test(text)) {
      setInvalid(true);
      floorFeedback("error");
      return;
    }
    setInvalid(false);
    const body: S["CountLineCountRequest"] = {
      ...(text.length > 0
        ? isUnitLine
          ? { counted_measure: text }
          : { counted_qty_base: text }
        : {}),
      ...(note.trim().length > 0 ? { note: note.trim() } : {}),
    };
    // AWAITED. `enqueue` resolves only once the record is on disk precisely so
    // that a caller who says "Saved" is telling the truth; firing it and
    // setting `saved` synchronously threw that guarantee away, and a rejected
    // durable write (full device, torn-down store) then left the operator with
    // a green "Saved on this device" and nothing saved anywhere.
    setState("saving");
    try {
      await queue.enqueue(
        { kind: "count_record", countId, lineId: line.id, body },
        { identity: serial ?? productName ?? locationPath },
      );
      setState("saved");
      draft.clear();
      floorFeedback("ok");
    } catch {
      setState("failed");
      floorFeedback("error");
    }
  }

  const recorded = queued !== undefined || server.length > 0 || (line.note ?? "").length > 0;

  return (
    <li className="floor-line" data-testid="floor-count-line" data-line-id={line.id}>
      <p className="floor-line-title">{serial ?? productName ?? shortId(line.product_id)}</p>
      {serial !== undefined && productName !== undefined ? (
        <p className="floor-line-sub">{productName}</p>
      ) : null}
      <p className="floor-line-sub">
        {locationPath ?? shortId(line.location_id)} · {uomLabel}
      </p>

      <div className="floor-field">
        <label htmlFor={`count-${line.id}`}>
          {isUnitLine
            ? t("inventory.floor.count.measureLabel", { uom: uomLabel })
            : t("inventory.floor.count.qtyLabel", { uom: uomLabel })}
        </label>
        <input
          id={`count-${line.id}`}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={value}
          onChange={(event) => {
            const next = event.target.value;
            draft.set((prev) => ({ ...prev, value: next }));
            setInvalid(false);
            setState("idle");
          }}
          data-testid="floor-count-input"
        />
      </div>

      <div className="floor-field">
        <label htmlFor={`note-${line.id}`}>{t("inventory.floor.count.noteLabel")}</label>
        <input
          id={`note-${line.id}`}
          type="text"
          autoComplete="off"
          value={note}
          placeholder={t("inventory.floor.count.notePlaceholder")}
          onChange={(event) => {
            const next = event.target.value;
            draft.set((prev) => ({ ...prev, note: next }));
            setState("idle");
          }}
          data-testid="floor-count-note"
        />
        <p className="floor-hint">{t("inventory.floor.count.skipHint")}</p>
      </div>

      {invalid ? (
        <p className="floor-scan-error" role="alert" data-testid="floor-count-invalid">
          {t("inventory.floor.scan.measureInvalid")}
        </p>
      ) : null}

      {parked ? (
        <p className="floor-line-parked" role="alert" data-testid="floor-count-line-parked">
          {t("inventory.floor.count.lineParked")}{" "}
          <Link to="/floor/tray">{t("inventory.floor.count.openTray")}</Link>
        </p>
      ) : null}

      <FloorButton
        variant={recorded ? "default" : "primary"}
        onClick={() => {
          void record();
        }}
        disabled={state === "saving" || (value.trim().length === 0 && note.trim().length === 0)}
        testId="floor-count-record"
      >
        {state === "saving"
          ? t("inventory.floor.count.recordSaving")
          : recorded
            ? t("inventory.floor.count.recorded")
            : t("inventory.floor.count.record")}
      </FloorButton>

      {state === "saved" ? (
        <p className="floor-line-saved" role="status" data-testid="floor-count-saved">
          {t("inventory.floor.count.saved")}
        </p>
      ) : null}
      {state === "failed" ? (
        <p className="floor-scan-error" role="alert" data-testid="floor-count-not-saved">
          {t("inventory.floor.sync.notSavedBody")}
        </p>
      ) : null}
    </li>
  );
}
